// Header.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import styles from "./Header.module.css";
import { logger } from "../utils/logger.js";
import { useAuth } from "../context/AuthContext";

// Get URLs from environment
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const FRONTEND_BASE_URL =
  process.env.REACT_APP_FRONTEND_URL || "http://localhost:3000";

const Header = ({ showLoginButton = true }) => {
  const navigate = useNavigate();
  const { freeAccessPeriod, logout } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [authError, setAuthError] = useState("");
  const redirectTimeoutRef = useRef(null);

  const token = localStorage.getItem("token");
  const isAuthenticated = !!token;

  // A page can be frozen into the browser's back-forward cache (bfcache) mid
  // navigation, with any pending setTimeout paused (not cancelled) — it
  // resumes counting when the page is later restored via the back/forward
  // buttons. Without this, an old timer armed by a previous click could fire
  // on a restored page, showing a stale error unrelated to anything the user
  // just did. On restore, cancel any leftover timer and reset to a clean state.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }
        setIsRedirecting(false);
        setAuthError("");
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    };
  }, []);

  // Extract user data from JWT token
  const getUserData = () => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      const userType = decoded.user_type || "student";
      const defaultName = userType === "teacher" ? "Teacher" : "Student";

      return {
        name: decoded.name || decoded.given_name || defaultName,
        class_name: decoded.class_name || "Class not assigned",
        user_type: userType,
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const userData = getUserData();

  const handleLogoClick = () => {
    if (isAuthenticated) {
      const userData = getUserData();

      if (userData && userData.user_type === "teacher") {
        logger.log("INSIDE TEACH DASH");
        navigate("/teacher/dashboard");
      } else {
        logger.log("INSIDE STU DASH");
        navigate("/dashboard");
      }
    } else {
      navigate("/login");
    }
  };

  const handleGoogleLogin = () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    setAuthError("");
    setIsRedirecting(true);
    window.location.href = `${API_BASE_URL}/auth/login`;

    // If we're still on this page after a few seconds, the navigation above
    // never actually happened (blocked, DNS/network failure, etc.) — this
    // fallback never runs on the happy path, since a successful redirect
    // unloads the page (and this component) before the timeout fires.
    redirectTimeoutRef.current = setTimeout(() => {
      setIsRedirecting(false);
      setAuthError("Couldn't reach the server. Please try again.");
      redirectTimeoutRef.current = null;
    }, 6000);
  };

  const handleLogout = async () => {
    try {
      // AuthContext.logout(): revokes server-side, clears its own token/user
      // React state, and removes localStorage("token") — keeps AuthContext
      // in sync instead of only clearing storage out from under it.
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = `${FRONTEND_BASE_URL}/login`;
    }
  };

  const getWelcomeMessage = () => {
    if (!userData) return "";

    if (userData.user_type === "teacher") {
      return `Welcome ${userData.name}`;
    } else {
      return `Welcome ${userData.name}, You are registered for: ${userData.class_name}`;
    }
  };

  return (
    <header className={styles.header}>
      {/* Logo Section */}
      <div
        className={styles.logoSection}
        onClick={handleLogoClick}
        style={{ cursor: "pointer" }}
      >
        <img
          src="/logojigyasaai.png"
          alt="Jigyasa AI Logo"
          className={styles.logoImage}
        />
        <div className={styles.logoText}>Jigyasa AI</div>
      </div>

      {/* Welcome Message */}
      {isAuthenticated && userData && (
        <div className={styles.welcomeMessage}>{getWelcomeMessage()}</div>
      )}

      {/* Auth Buttons */}
      <div className={styles.authButtons}>
        {isAuthenticated ? (
          <>
            {/* Upgrade button — hidden during free access period */}
            {!freeAccessPeriod && (
              <button 
                onClick={() => navigate('/pricing')} 
                className={styles.pricingButton}
                style={{
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                💎 Upgrade
              </button>
            )}
            <button onClick={handleLogout} className={styles.logoutButton}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </>
        ) : (
          showLoginButton && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "4px",
              }}
            >
              <button
                onClick={handleGoogleLogin}
                className={styles.headerButton}
                disabled={isRedirecting}
                style={{
                  opacity: isRedirecting ? 0.6 : 1,
                  cursor: isRedirecting ? "not-allowed" : "pointer",
                }}
              >
                {isRedirecting ? (
                  "Signing in..."
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path
                        fill="#4285f4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34a853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#fbbc05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#ea4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign in with Google
                  </>
                )}
              </button>
              {authError && (
                <span style={{ fontSize: "12px", color: "#dc2626" }}>
                  {authError}
                </span>
              )}
            </div>
          )
        )}
      </div>
    </header>
  );
};

export default Header;
